import { useCallback, useEffect, useRef, useState } from 'react'
import { useT } from '@/i18n'
import { useBoard } from '@/lib/board'
import { api, newIdempotencyKey, placeErrorKey, type MyBet } from '@/lib/api'
import { displayNumeric, ODD_OF, ticketRef } from '@/lib/keno-view'
import { Popper } from './Popper'
import type { Product } from './GameLot'

type Placed = { ok: true; ticket: string } | { ok: false; key: string; detail: string }

/** Rounds already rebet in this tab — the reference answers a second rebet with ERROR_REBET_DONE. */
const rebetDone = new Set<string>()

/**
 * The rebet button under the balls: shows my bets on this table's previous draw and places
 * the same again on the current one. Each bet goes through the normal bet endpoint with its
 * own idempotency key, so a partly-failed rebet never doubles the ones that went through.
 */
export function RebetSection({ product }: { product: Product }) {
  const t = useT()
  const board = useBoard()
  const [open, setOpen] = useState(false)
  const [bets, setBets] = useState<MyBet[] | null>(null)
  const [betting, setBetting] = useState(false)
  const [placed, setPlaced] = useState<Placed | null>(null)
  const [error, setError] = useState<string | null>(null)
  const okButton = useRef<HTMLButtonElement>(null)
  const closeTimer = useRef<number | undefined>(undefined)

  const close = useCallback(() => {
    setOpen(false)
    setBets(null)
    setError(null)
    window.clearTimeout(closeTimer.current)
  }, [])

  useEffect(() => { close() }, [product.drawId, close])
  useEffect(() => {
    if (product.status !== 'start-betting' && !betting) close()
  }, [product.status, betting, close])
  useEffect(() => () => window.clearTimeout(closeTimer.current), [])

  function openPopper() {
    if (product.status !== 'start-betting' || open) return
    setBetting(false)
    setPlaced(null)
    setError(null)
    setBets(null)
    setOpen(true)
    api.myBets(false, 1).then((all) => {
      // In the reference's bet-type order (ใหญ่, เล็ก, คี่ ... ดิน), not newest first.
      const order = (b: MyBet) => Number(ODD_OF[b.betType].slice(1))
      setBets(all
        .filter((b) => b.gameId === product.gameId && b.roundNumber === product.previousDrawId && b.status !== 'cancelled')
        .sort((x, y) => order(x) - order(y)))
    }).catch(() => setBets([]))
  }

  const total = bets?.reduce((s, b) => s + Number(b.amount), 0) ?? 0

  async function rebet() {
    if (!bets?.length || !product.roundId) return
    if (board.balance !== null && total > board.balance) {
      setError('ERROR_XAMOUNT')
      window.setTimeout(() => setError(null), 3000)
      return
    }
    setBetting(true)
    setPlaced(null)
    if (rebetDone.has(product.roundId)) {
      setPlaced({ ok: false, key: 'ERROR_REBET_DONE', detail: '405' })
    } else {
      rebetDone.add(product.roundId)
      let first = ''
      try {
        for (const b of bets) {
          const amount = Number(b.amount)
          const res = await api.placeBet(
            { roundId: product.roundId, betType: b.betType, amount: String(amount), limitId: board.tier.id },
            newIdempotencyKey(),
          )
          board.setBalance(Number(res.newBalance))
          board.addStake(product.roundId, b.betType, amount)
          first ||= ticketRef(res.bet.ticketNo, res.bet.id)
        }
        setPlaced({ ok: true, ticket: first })
      } catch (err) {
        setPlaced({ ok: false, key: placeErrorKey(err), detail: err instanceof Error ? err.message : '' })
      }
    }
    window.setTimeout(() => okButton.current?.focus(), 25)
    closeTimer.current = window.setTimeout(close, 5000)
  }

  const button = <div onClick={close} className="rebetbutton"><div /></div>

  return (
    <div className="rebetsection" onClick={openPopper}>
      {!open && <div className="rebetbutton"><div /></div>}{' '}
      {open && (
        <Popper
          boundaryId={`gamecont-${product.gameId}`}
          allowTop
          onDocumentClick={close}
          updateKey={`${bets?.length}-${betting}-${placed ? 1 : 0}-${error}`}
          reference={button}
        >
          <div className="popper-header">
            <span className="counter">{product.displayCountDown}</span>{' '}
            <b>{product.title}</b><br />
            {t('DRAW_NO')} {product.gCode}{product.drawId}
          </div>{' '}
          <form onSubmit={(e) => { e.preventDefault(); void rebet() }}>
            {betting ? (
              <div>
                {placed ? (
                  <div className="result">
                    <div className="summary">
                      {placed.ok ? (
                        <span className="success">{t('PLACE_SUCCESS')}<br /><small>[{t('REF')}: {placed.ticket}]</small></span>
                      ) : (
                        <span className="failed">{t(placed.key)} [{placed.detail}]</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="loading"><div className="loading-icon" /></div>
                )}
              </div>
            ) : (
              <div>
                {bets ? (
                  <div>
                    <div>{t('LASTBETSFOR')} {t('DRAW_NO')} {product.gCode}{product.previousDrawId}</div>{' '}
                    <div className="summary">
                      {bets.length ? (
                        <div>
                          {bets.map((b) => (
                            <div key={b.betId}><span>{t(ODD_OF[b.betType])}</span><span className="amt">{displayNumeric(b.amount, 2)}</span></div>
                          ))}{' '}
                          {total > 0 && (
                            <div className="gtotal"><span>{t('BETTOTAL')}</span> <span className="amt">{displayNumeric(total, 2)}</span></div>
                          )}
                        </div>
                      ) : (
                        <div className="error">{t('NOREBETLAST')}</div>
                      )}
                      {error && <div className="error">{t(error)}</div>}
                    </div>
                  </div>
                ) : (
                  <div className="loading"><div className="loading-icon" /></div>
                )}
              </div>
            )}{' '}
            {placed ? (
              <button ref={okButton} type="reset" onClick={close} className="ok-button">OK</button>
            ) : !betting ? (
              <div className="rebet-choices">
                {bets && (bets.length ? (
                  <>
                    <button className="bet-button" type="submit">{t('REBET')}</button>{' '}
                    <button type="reset" onClick={close} className="cancel-button">{t('CANCEL')}</button>
                  </>
                ) : (
                  <button type="reset" onClick={close} className="ok-button">OK</button>
                ))}
              </div>
            ) : null}
          </form>
        </Popper>
      )}{' '}
      <div style={{ clear: 'both', display: 'block' }} />
    </div>
  )
}
